import React, { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BasicInput, Alert } from '@dev/ee-components';
import { useTranslation } from 'react-i18next';
import { SetSendTestData } from '@/store/templateEditor/actions';
import { SendTestErrorNames, getTemplateEditor } from '@/store/templateEditor/types';
import { usePilotPlanConfig } from '@/services/hooks/usePilotPlanConfig';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { ContentWrapper, AlertWrapper } from './styles';

export const SendTestModal = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { sendTest } = useSelector(getTemplateEditor);
    const { sendToInput } = usePilotPlanConfig();
    const { isPilotPlan } = useCurrentPlanStatus();

    const sendTestCreditsHasError = !!sendTest.errors?.[SendTestErrorNames.NO_EMAIL_CREDITS];
    const sendTestSizeHasError = !!sendTest.errors?.[SendTestErrorNames.TEMPLATE_SIZE];

    const sendTestInvalidEmailsError = !!sendTest.errors?.[SendTestErrorNames.INVALID_EMAILS];
    const sendTestInvalidEmailsStatusError = !!sendTest.errors?.[SendTestErrorNames.INVALID_EMAILS_STATUS];

    const toEmailError = sendTestInvalidEmailsError || sendTestInvalidEmailsStatusError;

    const setToEmails = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(SetSendTestData({ ...sendTest, toEmails: event.target.value }));
    };

    const setFromName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(SetSendTestData({ ...sendTest, fromName: event.target.value }));
    };

    const setFromEmail = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(SetSendTestData({ ...sendTest, fromEmail: event.target.value }));
    };

    const setSubject = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(SetSendTestData({ ...sendTest, subject: event.target.value }));
    };

    const setPreviewText = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(SetSendTestData({ ...sendTest, previewText: event.target.value }));
    };

    const toInputErrorText = sendTestInvalidEmailsStatusError ? t('emailstatusinvalid') : t('emailinvalid');

    return (
        <ContentWrapper>
            <AlertWrapper>
                {sendTestCreditsHasError && <Alert severity="error" body={t('emailslimiterror')} />}
                {sendTestSizeHasError && <Alert severity="error" body={t('sendtemplatesizeerror')} />}
            </AlertWrapper>
            <BasicInput
                label={t('to')}
                id="input-to"
                fullWidth
                defaultValue={sendTest.toEmails}
                onChange={setToEmails}
                error={toEmailError}
                errorMessage={toInputErrorText}
                descriptionMessage={t('toinputdescriptionmessage')}
                {...(isPilotPlan && sendToInput)}
            />
            <BasicInput
                label={t('fromname')}
                defaultValue={sendTest.fromName}
                onChange={setFromName}
                id="send-test-input-fromname"
            />
            <BasicInput
                label={t('frommail')}
                defaultValue={sendTest.fromEmail}
                onChange={setFromEmail}
                id="send-test-input-fromemail"
            />
            <BasicInput
                label={t('subject')}
                defaultValue={sendTest.subject}
                onChange={setSubject}
                id="send-test-input-subject"
            />
            <BasicInput
                label={t('previewtext')}
                defaultValue={sendTest.previewText}
                onChange={setPreviewText}
                id="send-test-input-previewtext"
            />
        </ContentWrapper>
    );
};
